import {
  AppBar,
  Box,
  Button,
  IconButton,
  Toolbar,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  CloseFullscreen,
  FlipCameraIos,
  LiveTv,
  OpenInFull,
  ReplayOutlined,
} from "@mui/icons-material";
import EndBroadcastContainer from "./EndBroadcastContainer";

const WS_BASE = process.env.REACT_APP_WS_BASE;

export default function Broadcaster() {
  const [searchParams] = useSearchParams();
  const uri = searchParams.get("uri");
  const key = searchParams.get("key");
  const facingMode = searchParams.get("facingMode") || "user";

  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const socketRef = useRef(null);

  const [error, setError] = useState(null);
  const [compact, setCompact] = useState(false);

  const startStreaming = useCallback(
    async (newFacingMode = null) => {
      console.log("Starting stream...");
      const currentFacingMode = newFacingMode || facingMode;
      try {
        if (typeof MediaRecorder === "undefined" || !navigator.mediaDevices) {
          throw new Error(
            "MediaRecorder API or media devices are not supported in this browser."
          );
        }

        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: 720, max: 720 },
            height: { ideal: 720, max: 720 },
            frameRate: { ideal: 30, max: 30 },
            facingMode: currentFacingMode,
          },
          audio: {
            sampleRate: 44100,
            channelCount: 2,
            sampleSize: 16,
          },
        });

        console.log("Stream:", stream);
        console.log("VideoRef:", videoRef.current);
        videoRef.current.srcObject = stream;

        const mimeTypes = [
          'video/mp4;codecs="avc1.42E01E, mp4a.40.2"',
          "video/webm;codecs=vp8,opus",
          "video/webm;codecs=vp8",
          "video/webm",
        ];

        const mimeType = mimeTypes.find((type) =>
          MediaRecorder.isTypeSupported(type)
        );

        if (!mimeType) {
          throw new Error("No suitable MIME type found for MediaRecorder.");
        }

        console.log("Using MIME type:", mimeType);

        const videoBitrate = 3.5 * 1000 * 1000; // 5Mbps
        const audioBitrate = 128 * 1000; // 128kbps

        const mediaRecorder = new MediaRecorder(stream, {
          mimeType,
          videoBitsPerSecond: videoBitrate,
          audioBitsPerSecond: audioBitrate,
        });

        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0 && socketRef.current) {
            try {
              if (socketRef.current.readyState === WebSocket.OPEN) {
                console.log("Sending video-data...", event);
                socketRef.current.send(event.data);
              } else {
                console.warn("WebSocket is closed, data not sent.");
              }
            } catch (err) {
              console.error("Error sending video-data:", err);
            }
          }
        };
        // Start recording with the MediaRecorder, emitting dataavailable events every 2000ms (2 seconds).
        // This means the recorded video/audio data will be chunked and sent to the server every 2 seconds.
        mediaRecorder.start(100);
        mediaRecorderRef.current = mediaRecorder;
      } catch (err) {
        console.error("Error starting stream:", err);
        setError(err.message);
      }
    },
    [facingMode]
  );

  // Add function to handle facing mode change
  const handleFacingModeChange = useCallback(() => {
    const newFacingMode = facingMode === "user" ? "environment" : "user";
    console.log("Facing mode changed to:", newFacingMode);
    window.location.href = `?key=${key}&uri=${uri}&facingMode=${newFacingMode}`;
  }, [facingMode]);

  useEffect(() => {
    if (!uri) return;

    console.log("Connecting to WebSocket...", WS_BASE);
    const socket = new WebSocket(`${WS_BASE}?uri=${encodeURIComponent(uri)}`);
    socketRef.current = socket;

    socket.onopen = () => {
      console.log("WebSocket connected");
      setTimeout(() => startStreaming(), 500);
    };

    socket.onclose = () => {
      console.log("WebSocket disconnected");

      setError("Whoops. Sorry about that. Please click to restart streaming!");
    };

    socket.onerror = (error) => {
      console.error("WebSocket error:", error);
      setError("WebSocket connection failed");
    };

    // This cleanup function is returned from the useEffect hook.
    // It runs when the component unmounts or when the `uri` dependency changes.
    // Its purpose is to gracefully close and clean up all resources related to the live broadcast:
    // 1. Closes the WebSocket connection if it exists.
    // 2. Stops the MediaRecorder if it is currently recording.
    // 3. Stops all tracks of the video stream and clears the video element's srcObject.
    return () => {
      if (socketRef.current) {
        socketRef.current.close();
      }
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current.stop();
      }
      if (videoRef.current?.srcObject) {
        videoRef.current.srcObject.getTracks().forEach((track) => track.stop());
        videoRef.current.srcObject = null;
      }
    };
  }, [uri]);

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <ErrorContainer error={error} />
      </Box>
    );
  }

  return (
    <Box>
      <PageAppBar />

      <Box sx={{ mt: 1 }}>
        {/* Video Container */}
        <Box
          sx={{
            position: compact ? "fixed" : "relative",
            width: compact ? "120px" : "auto",
            height: compact ? "120px" : "auto",
            bottom: compact ? 100 : "auto",
            right: compact ? 10 : "auto",
            // height: "100%",
            borderRadius: "20px",
            overflow: "hidden",
            border: "1px solid #343332",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            aspectRatio: "1 / 1",
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: "100%",
            }}
          >
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                transform: facingMode === "user" ? "scaleX(-1)" : "none",
              }}
            />
            <IconButton
              onClick={handleFacingModeChange}
              size="small"
              sx={{
                position: "absolute",
                bottom: 10,
                left: 10,
                backgroundColor: "rgba(0, 0, 0, 0.3)",
              }}
            >
              <FlipCameraIos sx={{ color: "white", fontSize: 20 }} />
            </IconButton>
            <IconButton
              onClick={() => setCompact(!compact)}
              size="small"
              sx={{
                position: "absolute",
                bottom: 10,
                right: 10,
                backgroundColor: "rgba(0, 0, 0, 0.3)",
              }}
            >
              {compact ? (
                <OpenInFull sx={{ color: "white", fontSize: 20 }} />
              ) : (
                <CloseFullscreen sx={{ color: "white", fontSize: 20 }} />
              )}
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function ErrorContainer({ error }) {
  if (!error) return null;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexDirection: "column",
        gap: 1,
      }}
    >
      <Button
        variant="outlined"
        color="warning"
        onClick={() => window.location.reload()}
        startIcon={<ReplayOutlined />}
      >
        Restart stream
      </Button>
      <Typography color="caption" variant="caption">
        {error}
      </Typography>
    </Box>
  );
}

function PageAppBar() {
  const { artistKey } = useParams();

  return (
    <AppBar
      position="sticky"
      elevation={0}
      style={{ background: "rgba(19,19,19,.9)" }}
    >
      <Toolbar
        sx={{
          display: "flex",
          justifyContent: "space-between",
        }}
        disableGutters
      >
        <Box
          className="artist-logo"
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <LiveTv color="secondary" />

          <Typography
            style={{
              fontFamily: "'Montserrat Alternates', sans-serif",
              fontWeight: 600,
              fontSize: "1.4rem",
              marginBottom: "0px",
              marginLeft: "12px",
            }}
          >
            {artistKey} live
          </Typography>
        </Box>

        <EndBroadcastContainer />
      </Toolbar>
    </AppBar>
  );
}
